<template>
  <span
    ref="triggerRef"
    class="evidence-tag"
    :style="tagStyle"
    @click="handleOpenDetail"
    @mouseenter="showPopover"
    @mouseleave="hidePopover"
  >
    <i
      class="t-iconfont"
      :class="props.isFollowSource ? 'icon-edit2-ai-line' : 'icon-file-ai-line'"
    ></i>
    {{ props.fileName }}
  </span>
  <Teleport v-if="shouldRenderPopover" to="body">
    <span
      v-show="isPopoverVisible"
      ref="popoverRef"
      class="evidence-popover evidence-popover--floating"
      :class="{ 'evidence-popover--source': props.isFollowSource }"
      role="tooltip"
      :style="popoverStyle"
      @mouseenter="cancelHidePopover"
      @mouseleave="hidePopover"
    >
      <div
        v-if="props.isFollowSource"
        class="evidence-popover__source"
        type="button"
        @click="handleOpenDetail"
      >
        <div class="evidence-popover__link">
          <span class="evidence-popover__title">{{ props.deatilData.showName }}</span>
          <i class="icon-external-link-line t-iconfont"></i>
        </div>
        <div class="evidence-popover__meta">
          <span class="evidence-popover__meta-item">
            <i class="icon-user-line t-iconfont"></i>
            <span>{{ props.deatilData.creatorName }}</span>
          </span>
          <span class="evidence-popover__divider"></span>
          <span class="evidence-popover__meta-item">
            <i class="icon-time-line t-iconfont"></i>
            <span>{{ props.deatilData.creatorTime }}</span>
          </span>
        </div>
      </div>
      <template v-else>
        <span class="evidence-popover__title">{{ props.fileName }}</span>
        <span class="evidence-popover__content">{{ props.reason }}</span>
      </template>
    </span>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import emitter from '@/utils/mitt'

defineOptions({
  name: 'ReferenceCard'
})

const props = defineProps({
  fileName: {
    type: String,
    default: '无引用文件'
  },
  isFollowSource: {
    type: Boolean,
    default: false
  },
  reason: {
    type: String,
    default: ''
  },
  deatilData: {
    type: Object,
    default: () => ({})
  }
})

const hasReason = computed(() => Boolean(props.reason && props.reason.trim()))
const hasDetailData = computed(() => Boolean(props.deatilData?.id))
const canShowPopover = computed(() => hasReason.value || props.isFollowSource)

const tagStyle = computed(() =>
  props.isFollowSource ? { cursor: 'pointer' } : { cursor: 'default' }
)

const triggerRef = ref(null)
const popoverRef = ref(null)
const shouldRenderPopover = ref(false)
const isPopoverVisible = ref(false)
const popoverStyle = ref({})

let hideTimer = null
let isListening = false

const updatePopoverPosition = () => {
  const triggerEl = triggerRef.value
  const popoverEl = popoverRef.value

  if (!triggerEl || !popoverEl || !isPopoverVisible.value) return

  const gap = 8
  const viewportGap = 8
  const triggerRect = triggerEl.getBoundingClientRect()
  const popoverRect = popoverEl.getBoundingClientRect()
  const popoverWidth = popoverRect.width || 280
  const popoverHeight = popoverRect.height || 164
  const maxLeft = window.innerWidth - popoverWidth - viewportGap
  const left = Math.min(Math.max(triggerRect.left, viewportGap), Math.max(maxLeft, viewportGap))
  const top =
    triggerRect.top >= popoverHeight + gap + viewportGap
      ? triggerRect.top - popoverHeight - gap
      : triggerRect.bottom + gap

  popoverStyle.value = {
    top: `${window.scrollY + Math.max(top, viewportGap)}px`,
    left: `${window.scrollX + left}px`
  }
}

const addPositionListeners = () => {
  if (isListening) return
  window.addEventListener('resize', updatePopoverPosition)
  window.addEventListener('scroll', updatePopoverPosition, true)
  isListening = true
}

const removePositionListeners = () => {
  if (!isListening) return
  window.removeEventListener('resize', updatePopoverPosition)
  window.removeEventListener('scroll', updatePopoverPosition, true)
  isListening = false
}

const cancelHidePopover = () => {
  if (!hideTimer) return
  window.clearTimeout(hideTimer)
  hideTimer = null
}

const showPopover = async () => {
  if (!canShowPopover.value) return

  cancelHidePopover()
  shouldRenderPopover.value = true
  isPopoverVisible.value = true
  addPositionListeners()

  await nextTick()
  updatePopoverPosition()
}

const hidePopover = () => {
  cancelHidePopover()
  hideTimer = window.setTimeout(() => {
    isPopoverVisible.value = false
    shouldRenderPopover.value = false
    removePositionListeners()
  }, 80)
}

const handleOpenDetail = () => {
  if (!hasDetailData.value) return
  cancelHidePopover()
  isPopoverVisible.value = false
  shouldRenderPopover.value = false
  removePositionListeners()
  emitter.emit('openAiDetailSplit', {
    deatilData: props.deatilData
  })
}

onBeforeUnmount(() => {
  cancelHidePopover()
  removePositionListeners()
})
</script>

<style lang="scss">
/* 引用文件悬浮预览 */
.evidence-tag {
  /* 填充/Fill-2 一般 常规 白底悬浮 */
  --evidence-tag-bg: #f2f3f5;
  /* 文字/Text-3 次强调正文标题 */
  --evidence-tag-text: #646566;
  /* 线条/Line-2 一般线条 */
  --evidence-tag-border: #e5e6eb;
  /* 文字/Text-4 强调正文标题 */
  --evidence-popover-title: #323233;
  /* 文字/Text-2 次要信息 */
  --evidence-popover-content: #969799;

  position: relative;
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 2px 8px;
  margin-right: 4px;
  overflow: visible;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #969799;
  vertical-align: text-bottom;
  background: #f2f3f5;
  border: 1px solid #e5e6eb;
  border-radius: 8px;

  .t-iconfont {
    font-weight: 400;
    margin-right: 4px;
    font-size: 14px;
    line-height: 16px;
    font-style: normal !important;
  }
}
.evidence-tag:hover {
  color: #323233;
  background: #e5e6eb;
}

.evidence-tag:hover .evidence-popover {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.evidence-popover {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 12;
  box-sizing: border-box;
  width: 280px;
  max-height: 164px;
  padding: 12px;
  color: var(--evidence-popover-title);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 0 8px 4px rgba(0, 0, 0, 0.1);
  opacity: 0;
  visibility: hidden;
  transform: translateY(6px);
  transition:
    opacity 0.16s ease,
    transform 0.16s ease,
    visibility 0.16s ease;
}

.evidence-popover--floating {
  --evidence-popover-title: #323233;
  --evidence-popover-content: #969799;

  position: absolute;
  bottom: auto;
  left: auto;
  z-index: 2000;
  width: min(280px, calc(100vw - 16px));
  opacity: 1;
  visibility: visible;
  transform: none;
}

.evidence-popover--source {
  height: 64px;
  max-height: none;
  overflow: hidden;
}

.evidence-popover__title {
  display: block;
  margin-bottom: 4px;
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--evidence-popover-title);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.evidence-popover__source {
  cursor: pointer;
}

.evidence-popover__link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--evidence-popover-title);
  max-width: 100%;
  margin-bottom: 4px;

  .evidence-popover__title {
    min-width: 0;
    margin-bottom: 0;
  }
  .icon-external-link-line {
    color: #969799;
    font-size: 16px;
    margin-left: 4px;
    line-height: 20px;
  }
}

.evidence-popover__meta {
  display: flex;
  align-items: center;
  height: 16px;
  gap: 12px;
  overflow: hidden;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: var(--evidence-popover-title);
}

.evidence-popover__meta-item {
  display: inline-flex;
  align-items: center;
  min-width: 0;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .t-iconfont {
    flex: none;
    margin-right: 4px;
    font-size: 14px;
    line-height: 16px;
    color: var(--evidence-popover-content);
  }
}

.evidence-popover__divider {
  flex: none;
  width: 1px;
  height: 12px;
  background: #e5e6eb;
}

.evidence-popover__content {
  display: block;
  max-height: 116px;
  overflow: auto;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: var(--evidence-popover-content);
  white-space: pre-wrap;
}
</style>
